import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Flex, Modal, Skeleton, Typography, theme } from '@/shared/antd-imports';
import { PlayCircleOutlined } from '@ant-design/icons';
import { fetchEvidenceObjectUrl } from '@/store/updates-api';
import type { ClientUpdateEvidence } from '@/types/updates';

/** Loads a file through the API (x-client-token header) and gives an object URL; revoked on unmount. */
function useObjectUrl(updateId: string, evidenceId: string | null, thumbnail: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    if (!evidenceId) return undefined;
    let current: string | null = null;
    let isLive = true;
    setHasError(false);
    fetchEvidenceObjectUrl(updateId, evidenceId, thumbnail)
      .then(objectUrl => {
        if (!isLive) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        current = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => isLive && setHasError(true));
    return () => {
      isLive = false;
      if (current) URL.revokeObjectURL(current);
      setUrl(null);
    };
  }, [updateId, evidenceId, thumbnail]);
  return { url, hasError };
}

/**
 * Spec 28 §7.5 (stream abill): screenshots (inline) and recordings (player with controls) the provider shared in an
 * update. Never logs. Files are private: they are streamed with the client token, never opened by URL.
 */
export const UpdateEvidenceGallery: React.FC<{ updateId: string; evidence: ClientUpdateEvidence[] }> = ({ updateId, evidence }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<ClientUpdateEvidence | null>(null);
  if (!evidence.length) return null;
  return (
    <section aria-label={t('updates.evidence', { defaultValue: 'Screenshots and recordings' })}>
      <Typography.Title level={5}>{t('updates.evidence', { defaultValue: 'Screenshots and recordings' })}</Typography.Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
        {evidence.map(item => (
          <EvidenceTile key={item.id} updateId={updateId} item={item} onOpen={() => setOpen(item)} />
        ))}
      </div>
      <Modal
        open={!!open}
        onCancel={() => setOpen(null)}
        footer={null}
        title={open?.caption || t('updates.evidenceItem', { defaultValue: 'Attachment' })}
        width="min(960px, calc(100vw - 32px))"
        destroyOnHidden
      >
        {open && <EvidenceFull updateId={updateId} item={open} />}
      </Modal>
    </section>
  );
};

const EvidenceTile: React.FC<{ updateId: string; item: ClientUpdateEvidence; onOpen: () => void }> = ({ updateId, item, onOpen }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const thumb = useObjectUrl(updateId, item.kind === 'image' ? item.id : null, true);
  const label = item.caption || (item.kind === 'video' ? t('updates.recording', { defaultValue: 'Recording' }) : t('updates.screenshot', { defaultValue: 'Screenshot' }));
  return (
    <Flex vertical gap={6} data-testid="update-evidence-tile">
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('updates.openEvidence', { defaultValue: 'Open {{name}}', name: label })}
        style={{ padding: 0, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, background: token.colorFillTertiary, cursor: 'pointer', overflow: 'hidden', height: 120 }}
      >
        {item.kind === 'image' && thumb.url ? (
          <img src={thumb.url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            {item.kind === 'video' ? <PlayCircleOutlined aria-hidden style={{ fontSize: 32 }} /> : <Skeleton.Image active />}
          </Flex>
        )}
      </button>
      {item.caption && <Typography.Text style={{ fontSize: 13 }}>{item.caption}</Typography.Text>}
    </Flex>
  );
};

const EvidenceFull: React.FC<{ updateId: string; item: ClientUpdateEvidence }> = ({ updateId, item }) => {
  const { t } = useTranslation();
  const full = useObjectUrl(updateId, item.id, false);
  if (full.hasError) return <Alert type="warning" showIcon message={t('updates.evidenceError', { defaultValue: 'This file could not be opened. It may no longer be shared.' })} />;
  if (!full.url) return <Skeleton.Image active style={{ width: '100%', height: 240 }} />;
  return item.kind === 'video' ? (
    <video src={full.url} controls style={{ width: '100%', maxHeight: '70vh' }} aria-label={item.caption || t('updates.recording', { defaultValue: 'Recording' })} />
  ) : (
    <img src={full.url} alt={item.caption || t('updates.screenshot', { defaultValue: 'Screenshot' })} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />
  );
};
